import { generatePair, encrypt } from '@/helpers/encrypt'
import logger from '@/services/logger'

const DB_NAME = 'PROOFOFID'
const TABLE = 'KEYS'
const KEY_NAMES = {
  privateKey: 'PRIVATE_KEY',
  publicKey: 'PUBLIC_KEY'
}
const STREAM = 'auth'

export default {
  namespaced: true,
  state: {
    user: {},
    session: '',
    accessToken: '',
    idToken: '',
    refreshToken: '',
    isLoggedIn: false,
    authId: null,
    authUserUnsubscribe: null,
    confirmedUser: false,
    mfaRequired: false,
    changePassword: false
  },
  getters: {
    getMfaRequired: (state) => {
      return state.mfaRequired
    },
    authUser: (state) => {
      return state.user
    },
    getConfirmedUser: (state) => {
      // Use reactive state instead of sessionStorage for Vue reactivity
      // The mutation keeps state.authId and sessionStorage in sync
      return !!state.authId
    },
    getUserId: (state) => {
      return state.authId
    }
  },
  mutations: {
    setUser (state, user) {
      state.user = user
    },
    setMfaRequired (state, required) {
      state.mfaRequired = required
    },
    setAuthId (state, id) {
      state.authId = id
      if (id) {
        window.sessionStorage.setItem('USER_ID', id)
      } else {
        window.sessionStorage.removeItem('USER_ID')
      }
    },
    setConfirmedUser (state, bool = true) {
      state.confirmedUser = bool
    },
    setAuthUserUnsubscribe (state, unsubscribe) {
      state.authUserUnsubscribe = unsubscribe
    },
    setUserSession (state, session) {
      state.session = session
      if (session) {
        window.sessionStorage.setItem('SESSION', session)
      } else {
        window.sessionStorage.removeItem('SESSION')
      }
    },
    setUserAccessToken (state, accessToken) {
      state.accessToken = accessToken
      if (accessToken) {
        window.sessionStorage.setItem('ACCESS_TOKEN', accessToken)
      } else {
        window.sessionStorage.removeItem('ACCESS_TOKEN')
      }
    },
    setIdToken (state, idToken) {
      state.idToken = idToken || ''
      if (idToken) window.sessionStorage.setItem('ID_TOKEN', idToken)
      else window.sessionStorage.removeItem('ID_TOKEN')
    },
    setRefreshToken (state, refreshToken) {
      state.refreshToken = refreshToken || ''
      if (refreshToken) window.sessionStorage.setItem('REFRESH_TOKEN', refreshToken)
      else window.sessionStorage.removeItem('REFRESH_TOKEN')
    },
    setChangePassword (state, required) {
      state.changePassword = required
    }
  },
  actions: {
    // Auth v2 deliberately does not write the transient Cognito session anywhere.
    // The login component owns that value until authentication is complete.
    async signInV2 ({ dispatch }, { email, password }) {
      return dispatch('createData', { stream: 'auth/v2/signin', body: { email, password } }, { root: true })
    },
    async confirmEmailV2 ({ dispatch }, { email, code, password }) {
      return dispatch('createData', { stream: 'auth/v2/confirm-email', body: { email, code, password } }, { root: true })
    },
    async setupTotpV2 ({ dispatch }, { session }) {
      return dispatch('createData', { stream: 'auth/v2/setup-totp', body: { session } }, { root: true })
    },
    async verifyTotpV2 ({ dispatch }, { email, code, session }) {
      return dispatch('createData', { stream: 'auth/v2/verify-totp', body: { email, code, session } }, { root: true })
    },
    async confirmTotpV2 ({ dispatch }, { email, code, session }) {
      return dispatch('createData', { stream: 'auth/v2/confirm-totp', body: { email, code, session } }, { root: true })
    },
    async prepareV2PasswordChange ({ commit }, { email, result }) {
      if (!result?.accessToken) throw new Error('Password change session is missing. Please sign in again.')
      commit('setUserAccessToken', result.accessToken)
      commit('setIdToken', result.idToken)
      commit('setRefreshToken', result.refreshToken)
      commit('setUser', { email })
      commit('setConfirmedUser', true)
      commit('setChangePassword', true)
      const userId = result.UserID || result.userId || result.id || getJwtSubject(result.accessToken)
      if (userId) commit('setAuthId', userId)
    },
    async completeV2Authentication ({ commit }, result) {
      if (!result.accessToken) throw new Error('Authentication response did not include an access token')

      commit('setUserAccessToken', result.accessToken)
      commit('setIdToken', result.idToken)
      commit('setRefreshToken', result.refreshToken)
      commit('setConfirmedUser', true)

      const userId = result.UserID || result.userId || result.id || getJwtSubject(result.accessToken)
      if (userId) commit('setAuthId', userId)
    },
    async signUp ({ state, dispatch }, { email, password }) {
      // Initialize user object if it's null
      if (!state.user) {
        state.user = {}
      }
      state.user.email = email
      const result = await dispatch('createData', { stream: `${STREAM}/signup`, body: { email, password, username: email } }, { root: true })
      if (result.success && result.id) {
        state.user.id = result.id
      }
      return result
    },
    async confirmSignUp ({ commit, dispatch }, { email, code }) {
      return await dispatch('createData', { stream: `${STREAM}/confirmsignup`, body: { email, code } }, { root: true })
    },
    async signIn ({ commit, state, dispatch }, { email, password }) {
      // Initialize user object if it's null
      if (!state.user) {
        state.user = {}
      }
      state.user.email = email
      // Store email in sessionStorage for MFA page access after navigation
      window.sessionStorage.setItem('USER_EMAIL', email)

      const result = await dispatch('createData', { stream: `${STREAM}/signin`, body: { email, password } }, { root: true })

      // Store user ID from login result for MFA flows
      if (result.UserID || result.userId) {
        state.user.id = result.UserID || result.userId
      }

      if (result.session) {
        commit('setUserSession', result.session)
        // Also store in sessionStorage for page refresh persistence
        window.sessionStorage.setItem('SESSION', result.session)
      } else if (result.accessToken) {
        // Fixed: was using state.accessToken instead of result.accessToken
        commit('setUserAccessToken', result.accessToken)
        commit('setAuthId', state.user.id)
      }
      return result
    },
    // Enhanced session validation
    async validateSession ({ commit, state, dispatch }) {
      try {
        if (!state.session && !state.accessToken) {
          return false
        }

        // Make a simple request to validate current session
        const body = {}
        if (state.accessToken) {
          body.accessToken = state.accessToken
        } else {
          body.session = state.session
        }

        const result = await dispatch('createData', { stream: 'api/auth/validate-session', body }, { root: true })

        if (result.success) {
          return true
        } else if (result.error && result.error.includes('session')) {
          // Session is invalid - handle logout
          await dispatch('handleSessionInvalidation', 'Session expired or invalidated')
          return false
        }
      } catch (error) {
        return false
      }
      return false
    },

    // Resend Confirmation Code
    async resendConfirmationCode ({ _, state, dispatch }, payload = {}) {
      try {
        const email = payload?.email || state.user?.email
        const result = await dispatch('createData', { stream: 'auth/resendConfirmationCode', body: { email } }, { root: true })

        console.debug('[Auth] resendConfirmationCode result:', {
          success: result.success,
          message: result.message
        })

        if (result.success) {
          return { success: true, message: result.message }
        }

        throw new Error(result.message || 'Something went wrong, please try again later.')
      } catch (error) {
        return { success: false, error: error.message }
      }
    },

    // Handle session invalidation (logged out from another device)
    async handleSessionInvalidation ({ commit, dispatch }, reason) {
      // Clear local session data
      await dispatch('signOut')
      return true
    },

    // Enhanced sign-in with session handling
    async signInWithSessionHandling ({ commit, state, dispatch }, { email, password }) {
      try {
        const result = await dispatch('signIn', { email, password })

        // If sign-in succeeded, validate the session
        if (result.success || result.session || result.accessToken) {
          const isValid = await dispatch('validateSession')
          if (!isValid) {
            console.log('Session validation failed after sign-in')
          }
        }

        return result
      } catch (error) {
        return { success: false, error: error.message }
      }
    },

    // Periodic session check (call this from app lifecycle)
    async checkSessionStatus ({ state, dispatch }) {
      if (!state.user || (!state.session && !state.accessToken)) {
        return
      }

      try {
        const isValid = await dispatch('validateSession')
        if (!isValid) {
          console.log('Periodic session check failed - user logged out')
        }
      } catch (error) {
        console.error('Error in periodic session check:', error)
      }
    },
    async setupTOTP ({ commit, state, dispatch }) {
      if (state.user) {
        const body = { email: state.user.email }
        if (state.accessToken) {
          body.accessToken = state.accessToken
        } else {
          body.session = state.session
        }
        const result = await dispatch('createData', { stream: `${STREAM}/setuptotp`, body }, { root: true })
        if (result.error) {
          return result
        }
        if (result.session) {
          commit('setUserSession', result.session)
        }
        const params = new URL(result.url).searchParams
        return {
          code: params.get('secret'),
          url: result.url
        }
      } else {
        return null
      }
    },
    async verifySetupTOTP ({ commit, state, dispatch }, { code }) {
      const body = { totpToken: code, email: state.user.email }
      if (state.accessToken) {
        body.accessToken = state.accessToken
      } else {
        body.session = state.session
      }
      const result = await dispatch('createData', { stream: `${STREAM}/verifysetuptotp`, body }, { root: true })
      if (result.session) {
        commit('setUserSession', result.session)
      }
      if (result.success) {
        commit('setConfirmedUser', true)
      }
      return result
    },
    async confirmMFA ({ commit, state, dispatch }, { code }) {
      const requestPayload = {
        email: state.user.email,
        code,
        session: state.session
      }

      // Validate required fields before sending
      if (!requestPayload.email) {
        return { success: false, error: 'Email is required for MFA confirmation' }
      }
      if (!code) {
        return { success: false, error: 'MFA code is required' }
      }
      if (!requestPayload.session) {
        return { success: false, error: 'Session is required for MFA confirmation. Please sign in again.' }
      }

      const result = await dispatch('createData', { stream: `${STREAM}/confirmmfa`, body: requestPayload }, { root: true })

      if (result.success) {
        commit('setConfirmedUser', true)
        // Handle different field names for user ID
        const userId = result.id || result.UserID || result.userId
        if (userId) {
          commit('setAuthId', userId)
        }
        if (result.accessToken) {
          commit('setUserAccessToken', result.accessToken)
        }
        // Only initialize authorization if not in password change flow
        if (result.challengeName !== 'NEW_PASSWORD_REQUIRED') {
          await dispatch('authorization/initializeAuthorization', null, { root: true })
        } else {
          // Set flag for password change requirement
          commit('setChangePassword', true)
        }
      }
      return result
    },
    async confirmSMSMFA ({ commit, state, dispatch }, { code }) {
      const result = await dispatch('createData', { stream: `${STREAM}/confirmSMSMFA`, body: { email: state.user.email, code, session: state.session } }, { root: true })
      if (result.success) {
        commit('setConfirmedUser', true)
        // Handle both possible field names for access token
        if (result.access_token) {
          commit('setUserAccessToken', result.access_token)
        } else if (result.accessToken) {
          commit('setUserAccessToken', result.accessToken)
        }
        // Handle different field names for user ID
        let userId = result.id || result.UserID || result.userId

        // If no user ID in response, try to extract from JWT token or use existing state
        if (!userId && result.access_token) {
          try {
            // Decode JWT token to get user ID (sub field)
            const payload = JSON.parse(atob(result.access_token.split('.')[1]))
            userId = payload.sub
          } catch (e) {
            // JWT decoding failed - this is expected for malformed tokens
            // Use proper logging mechanism for debugging JWT decode failures
            logger.debug('JWT decode failed', { error: e.message })
          }
        }

        // Fallback to existing user state
        if (!userId && state.user?.id) {
          userId = state.user.id
        }

        if (userId) {
          commit('setAuthId', userId)
        }
      }
      return result
    },
    async resetPassword ({ commit, state, dispatch }, { oldPassword, newPassword }) {
      if (oldPassword && newPassword) {
        const result = await dispatch('createData', { stream: 'api/user/changepassword', body: { username: state.user.email, accessToken: state.accessToken, oldPassword, newPassword } }, { root: true })
        return result
      }
      return false
    },
    async signOut ({ commit }) {
      try {
        // Clear all storage to prevent session fixation
        const keys = Object.keys(localStorage)
        keys.forEach(key => {
          if (key.includes('CognitoIdentityServiceProvider.')) {
            localStorage.removeItem(key)
          }
        })

        // Reset all auth state
        commit('setUser', null)
        commit('setAuthId', null)
        commit('setConfirmedUser', false)
        commit('setUserSession', null)
        commit('setUserAccessToken', null)
        commit('setIdToken', null)
        commit('setRefreshToken', null)
        commit('setChangePassword', false)
        commit('setMfaRequired', false)

        // Clear authorization state
        this.commit('authorization/clearAuthorization')

        // Clear session storage completely
        window.sessionStorage.clear()

        return true
      } catch (error) {
        return false
      }
    },

    // Security: Session regeneration after authentication
    async regenerateSession ({ commit }) {
      try {
        // Clear existing session data to prevent fixation
        const currentUserId = window.sessionStorage.getItem('USER_ID')
        const currentSession = window.sessionStorage.getItem('SESSION')
        const currentAccessToken = window.sessionStorage.getItem('ACCESS_TOKEN')

        // Clear all session storage
        window.sessionStorage.clear()

        // Re-establish with fresh session identifiers
        if (currentUserId) {
          window.sessionStorage.setItem('USER_ID', currentUserId)
        }
        if (currentSession) {
          window.sessionStorage.setItem('SESSION', currentSession)
        }
        if (currentAccessToken) {
          window.sessionStorage.setItem('ACCESS_TOKEN', currentAccessToken)
        }

        return true
      } catch (error) {
        return false
      }
    },
    async get ({ commit, dispatch }) {
      const userId = commit('getUserId')
      const user = await dispatch('users/get', {}, { root: true })
      commit('setUser', user)
      commit('setAuthId', userId)
      return user
    },
    async getKeyStatus ({ dispatch, state }) {
      const userId = state.authId
      const privateKey = await dispatch('getByUserId', { database: DB_NAME, table: TABLE, id: KEY_NAMES.privateKey, userId }, { root: true })
      const publicKey = await dispatch('getByUserId', { database: DB_NAME, table: TABLE, id: KEY_NAMES.publicKey, userId }, { root: true })
      if (!privateKey || !publicKey) {
        return false
      }
      return true
    },
    async generateKeyPair ({ dispatch, state }) {
      const userId = await state.authId
      const privateKey = await dispatch('getByUserId', { database: DB_NAME, table: TABLE, id: KEY_NAMES.privateKey, userId }, { root: true })
      const publicKey = await dispatch('getByUserId', { database: DB_NAME, table: TABLE, id: KEY_NAMES.publicKey, userId }, { root: true })
      if (!privateKey || !publicKey) {
        return await generateKeys({ dispatch, state })
      }
    },
    async regenerateKeyPair ({ dispatch, state }) {
      return await generateKeys({ dispatch, state })
    },
    async createDeviceID ({ dispatch }) {
      if (!localStorage.getItem('DEVICE_ID')) {
        const deviceId = await dispatch('generateDeviceId', {}, { root: true })
        localStorage.setItem('DEVICE_ID', deviceId)
      }
    },
    async initAuthentication ({ commit, dispatch }) {
      try {
        const session = window.sessionStorage.getItem('SESSION')
        const accessToken = window.sessionStorage.getItem('ACCESS_TOKEN')
        const idToken = window.sessionStorage.getItem('ID_TOKEN')
        const refreshToken = window.sessionStorage.getItem('REFRESH_TOKEN')
        // v2 does not always include a user id in its response. Recover it from
        // the persisted access token so a browser reload restores auth state.
        const id = window.sessionStorage.getItem('USER_ID') || getJwtSubject(accessToken)

        if (accessToken || session) {
          if (session) {
            commit('setUserSession', session)
          }
          if (accessToken) {
            commit('setUserAccessToken', accessToken)
          }
          if (idToken) commit('setIdToken', idToken)
          if (refreshToken) commit('setRefreshToken', refreshToken)
          if (!id) return null
          commit('setAuthId', id)

          // Role/menu access comes from the backend, not the optional local
          // profile cache. Restore it even when that cache has not loaded yet.
          commit('setConfirmedUser', true)
          await dispatch('authorization/initializeAuthorization', null, { root: true })

          const user = await dispatch('users/get', {}, { root: true })
          if (user) {
            commit('setUser', user)
            commit('setAuthId', id)
          }
          return user
        }
        return null
      } catch (error) {
        return null
      }
    }
  }
}

function getJwtSubject (token) {
  try {
    const payload = token?.split('.')[1]
    if (!payload) return null
    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/')
    const paddedPayload = normalizedPayload.padEnd(normalizedPayload.length + ((4 - normalizedPayload.length % 4) % 4), '=')
    return JSON.parse(atob(paddedPayload)).sub || null
  } catch (_) {
    return null
  }
}

async function generateKeys ({ dispatch, state }) {
  const userId = window.sessionStorage.getItem('USER_ID')
  if (userId) {
    const keys = await generatePair(userId)
    const privateKey = await encrypt(state, keys.privateKey, userId)
    const publicKey = await encrypt(state, keys.publicKey, userId)
    if (privateKey && publicKey) {
      const request = {
        userId,
        publicKey: keys.publicKey,
        deviceId: localStorage.getItem('DEVICE_ID')
      }
      const requestResponse = await dispatch('createData', { stream: 'api/public/key/create', body: request }, { root: true })
      await dispatch('add', {
        database: DB_NAME,
        table: TABLE,
        record: { id: KEY_NAMES.privateKey, userId, data: privateKey }
      }, { root: true })
      await dispatch('add', {
        database: DB_NAME,
        table: TABLE,
        record: { id: KEY_NAMES.publicKey, userId, data: publicKey }
      }, { root: true })
      return requestResponse
    } else {
      throw new Error('Key saving was unsuccessful')
    }
  }
}
